/* eslint-disable camelcase */

import { NETWORK_ERROR_PATTERNS } from "common/utils/honeybadger/log-upload-error";

// `NETWORK_ERROR_PATTERNS` is primarily client/network noise,
// For dtf init-meta error, we keep server errors visible and only suppress clear client noise.
const CLIENT_NOISE_PATTERNS = NETWORK_ERROR_PATTERNS.filter(
  pattern => pattern.toString() !== "/timeout/i"
);

const NOISE_ERROR_PATTERNS = [...CLIENT_NOISE_PATTERNS, /Client Closed Request/i, /CanceledError/i];

const CLOUDFRONT_BLOCK_PATTERN = /cloudfront/i;
const ROBOTS_BLOCK_PATTERN = /robots disallow/i;
const IGNORED_STATUSES = new Set([429, 499]);

const isNonEmptyString = value => typeof value === "string" && value.length > 0;

const safeJsonStringify = value => {
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const toResponseBodyString = error => {
  const response = error?.response;

  if (typeof response === "string") return response;

  // Axios-style shape uses `error.response.data` for body (string or JSON object).
  if (response?.data) {
    return typeof response.data === "string" ? response.data : safeJsonStringify(response.data);
  }

  if (
    response &&
    typeof response === "object" &&
    !("status" in response) &&
    !("data" in response)
  ) {
    return safeJsonStringify(response);
  }

  return "";
};

const toSearchableString = error =>
  [error?.message, toResponseBodyString(error), error?.stack].filter(isNonEmptyString).join(" | ");

/**
 * Returns true if the error is relevant enough to notify Honeybadger.
 *
 * Keep the filtering logic centralized here so call sites can stay minimal.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export default function isRelevantHoneybadgerError(error) {
  const status = error?.status ?? error?.response?.status;
  const haystack = toSearchableString(error);

  // Ignore common non-actionable classes.
  if (IGNORED_STATUSES.has(status)) return false;
  if (status === 403 && CLOUDFRONT_BLOCK_PATTERN.test(haystack)) return false;
  if (status === 403 && ROBOTS_BLOCK_PATTERN.test(haystack)) return false;

  // Keep server errors visible in HB.
  if (typeof status === "number" && status >= 500) return true;

  if (NOISE_ERROR_PATTERNS.some(pattern => pattern.test(haystack))) return false;

  return true;
}

/* eslint-enable camelcase */
